import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import {
  listUsers,
  inviteUser,
  updateUser,
  deleteUser,
  type UserListItem,
} from '../api/auth';
import { formatRelativeTime } from '../utils/formatters';

function RoleBadge({ role }: { role: 'admin' | 'user' }) {
  const styles = {
    admin: 'bg-purple-100 text-purple-700',
    user: 'bg-blue-100 text-blue-700',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role]}`}>
      {role === 'admin' ? 'Admin' : 'User'}
    </span>
  );
}

function StatusBadge({ status }: { status: 'active' | 'invited' | 'disabled' }) {
  const styles = {
    active: 'bg-green-100 text-green-700',
    invited: 'bg-yellow-100 text-yellow-700',
    disabled: 'bg-gray-100 text-gray-500',
  };
  const labels = {
    active: 'Active',
    invited: 'Invited',
    disabled: 'Disabled',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

export function AdminUsersPage() {
  const { sessionToken, isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user'>('user');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  async function loadUsers() {
    if (!sessionToken) return;

    try {
      const response = await listUsers(sessionToken);
      setUsers(response.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, [sessionToken]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionToken) return;

    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const response = await inviteUser(sessionToken, {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim() || undefined,
        role: inviteRole,
      });
      setInviteSuccess(response.message);
      setInviteEmail('');
      setInviteName('');
      setInviteRole('user');
      loadUsers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  }

  async function handleToggleRole(user: UserListItem) {
    if (!sessionToken) return;

    setActionLoading(user.id);
    try {
      const newRole = user.role === 'admin' ? 'user' : 'admin';
      await updateUser(sessionToken, user.id, { role: newRole });
      loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleToggleStatus(user: UserListItem) {
    if (!sessionToken) return;

    setActionLoading(user.id);
    try {
      const newStatus = user.status === 'disabled' ? 'active' : 'disabled';
      await updateUser(sessionToken, user.id, { status: newStatus });
      loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(user: UserListItem) {
    if (!sessionToken) return;
    if (!confirm(`Are you sure you want to disable ${user.email}?`)) return;

    setActionLoading(user.id);
    try {
      await deleteUser(sessionToken, user.id);
      loadUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  }

  if (!isAdmin) {
    return (
      <PageContainer title="Admin - Users">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">
          Access denied. Admin privileges required.
        </div>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer title="Admin - Users">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Admin - Users">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Admin - Users"
      subtitle="Manage dashboard user access and permissions"
    >
      {/* Invite User Button */}
      <div className="mb-6">
        {!showInviteForm ? (
          <Button onClick={() => setShowInviteForm(true)}>
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Invite User
          </Button>
        ) : (
          <Card>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invite New User</h3>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Email Address"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@company.com"
                  required
                />
                <Input
                  label="Name (optional)"
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="role"
                      value="user"
                      checked={inviteRole === 'user'}
                      onChange={() => setInviteRole('user')}
                      className="mr-2"
                    />
                    <span className="text-sm">User</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="role"
                      value="admin"
                      checked={inviteRole === 'admin'}
                      onChange={() => setInviteRole('admin')}
                      className="mr-2"
                    />
                    <span className="text-sm">Admin</span>
                  </label>
                </div>
              </div>

              {inviteError && (
                <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                  {inviteError}
                </div>
              )}

              {inviteSuccess && (
                <div className="bg-green-50 text-green-600 text-sm p-3 rounded-lg">
                  {inviteSuccess}
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" loading={inviting}>
                  Send Invitation
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteError(null);
                    setInviteSuccess(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>

      {/* Users List */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Dashboard Users ({users.length})
        </h2>
        {users.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invited By
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => {
                  const isCurrentUser = user.id === currentUser?.id;
                  const isLoading = actionLoading === user.id;

                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.name || 'No name'}
                            {isCurrentUser && (
                              <span className="ml-2 text-xs text-gray-500">(you)</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <RoleBadge role={user.role} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login_at
                          ? formatRelativeTime(user.last_login_at)
                          : 'Never'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {user.invited_by_name || user.invited_by_email || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {!isCurrentUser && (
                          <div className="flex justify-end gap-2">
                            {isLoading ? (
                              <Spinner size="sm" />
                            ) : (
                              <>
                                <button
                                  onClick={() => handleToggleRole(user)}
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                  title={`Change to ${user.role === 'admin' ? 'User' : 'Admin'}`}
                                >
                                  {user.role === 'admin' ? 'Demote' : 'Promote'}
                                </button>
                                <button
                                  onClick={() => handleToggleStatus(user)}
                                  className="text-xs text-yellow-600 hover:text-yellow-800"
                                  title={user.status === 'disabled' ? 'Enable' : 'Disable'}
                                >
                                  {user.status === 'disabled' ? 'Enable' : 'Disable'}
                                </button>
                                {user.status !== 'disabled' && (
                                  <button
                                    onClick={() => handleDelete(user)}
                                    className="text-xs text-red-600 hover:text-red-800"
                                    title="Delete user"
                                  >
                                    Delete
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No users found</p>
        )}
      </Card>
    </PageContainer>
  );
}
